/**
 * @file RegisterViewState.js
 * @description Manages the state for pending registration requests, including form steps, modals, and visibility flags.
 */

export default class RegisterViewState {
  /**
   * Indicates whether the pending request view is visible.
   * @type {boolean}
   */
  isVisible = false;

  /**
   * Data collected from the first step of the form.
   * @type {Object|null}
   */
  dataFormStep1 = null;

  /**
   * Data collected from the first step of the form in tutorized mode.
   * @type {Object|null}
   */
  dataFormStep2Tutorize = null;

  /**
   * Data collected from the second step of the form.
   * @type {Object|null}
   */
  dataFormStep2 = null;

  /**
   * Determines if the birth date is required.
   * @type {boolean}
   */
  birthDateRequirement = true;

  /**
   * Indicates if a second surname disclaimer is needed.
   * @type {boolean}
   */
  secondSurnameDisclaimer = true;

  /**
   * Configuration and state for the registration form.
   * @type {Object}
   */
  formRegister = {
    isTutorize: false,
    step: 1, // 1 Datos, 2 Contacto, 3 Confirmación
    stepTutorize: 1, // 1 Tutor, 2 Menor, 3 Contacto, 4 Confirmación
    tabsSteps: [
      {
        title: 'Datos',
        iconSuccess: null,
        isValid: true,
        step: 1,
      },
      {
        title: 'Contacto',
        iconSuccess: null,
        isValid: true,
        step: 2,
      },
      {
        title: 'Confirmación',
        iconSuccess: null,
        isValid: true,
        step: 3,
      },
    ],
    temporalToken: null,
  };

  /**
   * Configuration and state for the policy modal.
   * @type {Object}
   */
  policyModal = {
    isVisible: false,
  };

  /**
   * Configuration and state for the support number information modal.
   * @type {Object}
   */
  supportNumberInfo = {
    isVisible: false,
  };

  /**
   * Configuration and state for the identification number information modal.
   * @type {Object}
   */
  infoIdentificationNumber = {
    isVisible: false,
  };

  /**
   * The title of the page, set based on whether the registration is tutorized.
   * @type {string}
   */
  titlePage = '';

  /**
   * Creates an instance of PendingRequestViewState.
   *
   * @param {boolean} isTutorizeRegister - Indicates if the registration process is tutorized.
   */
  constructor(isTutorizeRegister = false) {
    this.setTutorizeRegister(isTutorizeRegister);
    this.buildSteps();
  }

  /**
   * Sets the tutorize registration flag and updates the page title accordingly.
   *
   * @param {boolean} isTutorizeRegister - Indicates if the registration process is tutorized.
   * @throws {TypeError} Throws if the provided parameter is not a boolean.
   */
  setTutorizeRegister(isTutorizeRegister) {
    if (typeof isTutorizeRegister !== 'boolean') {
      throw new TypeError(
        `Invalid type for isTutorizeRegister: expected boolean, received ${typeof isTutorizeRegister}`,
      );
    }

    this.formRegister.isTutorize = isTutorizeRegister;
    this.titlePage = isTutorizeRegister ? 'Datos personales del tutor' : 'Datos personales';
  }

  /**
   * Toggles the visibility of the pending request view.
   */
  toggleVisibility() {
    this.isVisible = !this.isVisible;
  }

  /**
   * Sets the visibility of the pending request view.
   * @param {boolean} isVisible - The visibility state to set.
   */
  setVisibility(isVisible) {
    this.isVisible = isVisible;
  }

  /**
   * Resets the form registration state to its initial values.
   */
  resetFormRegister() {
    this.formRegister = {
      step: 1,
      isTutorize: this.formRegister.isTutorize,
      stepTutorize: 1,
      tabsSteps: this.formRegister.tabsSteps.map((tab) => ({
        ...tab,
        iconSuccess: null,
        isValid: true,
      })),
      temporalToken: null,
    };
  }

  /**
   * Builds the steps for the registration form based on whether the registration is tutorized.
   */
  buildSteps() {
    if (this.formRegister.isTutorize) {
      this.formRegister.tabsSteps = [
        {
          title: 'Tutor',
          iconSuccess: null,
          isValid: true,
          step: 1,
        },
        {
          title: 'Menor',
          iconSuccess: null,
          isValid: true,
          step: 2,
        },
        {
          title: 'Contacto',
          iconSuccess: null,
          isValid: true,
          step: 3,
        },
        {
          title: 'Confirmación',
          iconSuccess: null,
          isValid: true,
          step: 4,
        },
      ];
    } else {
      this.formRegister.tabsSteps = [
        {
          title: 'Datos',
          iconSuccess: null,
          isValid: true,
          step: 1,
        },
        {
          title: 'Contacto',
          iconSuccess: null,
          isValid: true,
          step: 2,
        },
        {
          title: 'Confirmación',
          iconSuccess: null,
          isValid: true,
          step: 3,
        },
      ];
    }
  }

  /**
   * Sets data for a specific form step.
   *
   * @param {number} step - The form step number (e.g., 1 or 2).
   * @param {Object} data - The data to set for the specified step.
   * @throws {RangeError} Throws if the step number is invalid.
   * @throws {TypeError} Throws if the data provided is not an object.
   */
  setDataFormStep(step, data) {
    if (typeof data !== 'object' || data === null) {
      throw new TypeError(
        `Invalid type for data: expected object, received ${typeof data}`,
      );
    }

    switch (step) {
      case 1:
        this.dataFormStep1 = data;
        break;
      case 2:
        this.dataFormStep2 = data;
        break;
      default:
        throw new RangeError(
          `Invalid step number: expected 1 or 2, received ${step}`,
        );
    }
  }

  /**
   * Updates the validity of a specific tab step.
   *
   * @param {number} index - The index of the tab step to update.
   * @param {boolean} isValid - The validity status to set.
   * @throws {RangeError} Throws if the index is out of bounds.
   * @throws {TypeError} Throws if isValid is not a boolean.
   */
  updateTabStepValidity(index, isValid) {
    if (typeof isValid !== 'boolean') {
      throw new TypeError(
        `Invalid type for isValid: expected boolean, received ${typeof isValid}`,
      );
    }

    if (index < 0 || index >= this.formRegister.tabsSteps.length) {
      throw new RangeError(
        `Tab step index out of bounds: received ${index}, but only ${this.formRegister.tabsSteps.length} tabs are available`,
      );
    }

    this.formRegister.tabsSteps[index].isValid = isValid;
  }

  /**
   * Sets the temporal token for the registration form.
   *
   * @param {string} token - The temporal token to set.
   * @throws {TypeError} Throws if the token is not a string.
   */
  setTemporalToken(token) {
    if (typeof token !== 'string') {
      throw new TypeError(
        `Invalid type for token: expected string, received ${typeof token}`,
      );
    }

    this.formRegister.temporalToken = token;
  }

  /**
   * Toggles the visibility of a specified modal.
   *
   * @param {string} modalName - The name of the modal to toggle ('policy', 'supportNumberInfo', 'infoIdentificationNumber').
   * @throws {Error} Throws if an invalid modal name is provided.
   */
  toggleModalVisibility(modalName) {
    switch (modalName) {
      case 'policy':
        this.policyModal.isVisible = !this.policyModal.isVisible;
        break;
      case 'supportNumberInfo':
        this.supportNumberInfo.isVisible = !this.supportNumberInfo.isVisible;
        break;
      case 'infoIdentificationNumber':
        this.infoIdentificationNumber.isVisible = !this.infoIdentificationNumber.isVisible;
        break;
      default:
        throw new Error(`Invalid modal name: ${modalName}`);
    }
  }

  /**
   * Retrieves the current state of the registration form.
   *
   * @returns {Object} The current form registration state.
   */
  getFormRegisterState() {
    return {...this.formRegister};
  }

  /**
   * Retrieves the current title of the page.
   *
   * @returns {string} The page title.
   */
  getTitlePage() {
    return this.titlePage;
  }

  /**
   * Retrieves the current visibility state of the pending request view.
   */
  checkTutorizeInStep(step, tutorize = this.formRegister.isTutorize) {
    return tutorize === this.formRegister.isTutorize && this.formRegister.step === step;
  }
}

